import { useContext, useCallback, useMemo } from 'react';
import { graphql } from 'react-relay';

import { ConfluencePageAri } from '@atlassian/ari';

import { CREATE_PAGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { OnContentUpdatedResponse } from '@confluence/create-dialog-context';
import { CreateDialogContext } from '@confluence/create-dialog-context';
import { CREATE_PAGE_VARIABLES_ACTION_LEGACY } from '@confluence/named-routes/entry-points/routes.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import {
	CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME,
	IN_EDITOR_TEMPLATES_QUERY_OPEN,
	IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME,
} from '@confluence/template-utils/entry-points/urlUtils.ts';
import { addQueryParams } from '@confluence/route-manager-utils/entry-points/url.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	Creatable,
	CanHandle,
	Handle,
	TemplateBodyDetails,
} from '../createContentFromTemplateTypes.ts';
import { processDraftUrl } from '../helpers.ts';
import { useExperimentalPublishPageByTemplateMutation } from '../queries/useExperimentalPublishPageByTemplateMutation.ts';
import type {
	GetTemplateBodyContentQuery as GetTemplateBodyResponse,
	GetTemplateBodyContentQueryVariables,
} from '../queries/__types__/GetTemplateBodyContentQuery.ts';
import { GetTemplateBodyContentQuery } from '../queries/GetTemplateBodyContent.graphql.ts';

import { useNavigation } from './useNavigation.ts';
import type {
	useTemplateCreatableUpdateInstanceMutation$data,
	useTemplateCreatableUpdateInstanceMutation$variables,
} from './__generated__/useTemplateCreatableUpdateInstanceMutation.graphql.ts';

const getTemplateBodyDetails = async (templateId: string): Promise<TemplateBodyDetails> => {
	// this has to be done via getApolloClient() because neither useQuery or useLazyQuery return a promise with the data, they return void.
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	const templateBodyResponse = (
		await getApolloClient().query<GetTemplateBodyResponse, GetTemplateBodyContentQueryVariables>({
			query: GetTemplateBodyContentQuery,
			variables: {
				templateId,
			},
		})
	).data;

	const adfString = templateBodyResponse?.template?.body?.atlas_doc_format?.value || '';

	const hasVariables = adfString.indexOf('com.atlassian.confluence.template') !== -1;

	const editorVersion = templateBodyResponse?.template?.editorVersion || '';

	return { hasVariables, editorVersion };
};

export const useTemplateCreatable = (): Creatable => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const navigate = useNavigation();
	const { openTemplateVariables } = useContext(CreateDialogContext);
	const isEditorPage = useIsEditorPage();

	const { cloudId } = useSessionData();

	const [createFromTemplateMutation] = useExperimentalPublishPageByTemplateMutation();

	const [updateFromTemplateMutation] = useMutationInterop<
		useTemplateCreatableUpdateInstanceMutation$data,
		useTemplateCreatableUpdateInstanceMutation$variables
	>(graphql`
		mutation useTemplateCreatableUpdateInstanceMutation(
			$pageAri: ID!
			$templateId: String
			$title: String
			$spaceKey: String
		) {
			confluence_updateInstance(
				pageAri: $pageAri
				input: {
					contentBlueprintSpec: {
						templateId: $templateId
						context: { name: $title, spaceKey: $spaceKey }
					}
					contentStatus: DRAFT
					nextVersion: 1
				}
				expand: "metadata.simple.isFabric,space,version" # Have to include space and version in expand as well to avoid error in backend
			) {
				content {
					id
					type
					title
					type
					space {
						key
					}
					links {
						editui
					}
					metadata {
						simple {
							isFabric
						}
					}
				}
			}
		}
	`);

	const canHandle: CanHandle = useCallback((template) => !!template.templateId, []);

	const handle: Handle = useCallback(
		async (template, spaceKey, options = {}) => {
			// casting because i know it exists from canHandle
			const templateId = template.templateId as string;
			const {
				contentId,
				parentPageId,
				title,
				subType,
				onContentUpdated,
				onVariableInputError,
				beforeUpdateRequest,
				forceTemplatePanelOpen,
				openContentInNewTab,
			} = options;

			const { hasVariables, editorVersion } = await getTemplateBodyDetails(templateId);
			if (hasVariables) {
				const onContentUpdatedWrapper = (response: OnContentUpdatedResponse) => {
					if (onContentUpdated) {
						onContentUpdated(response, template, spaceKey, contentId || '');
					}
				};

				if (editorVersion === 'v2') {
					openTemplateVariables({
						templateId,
						spaceKey,
						parentPageId: parentPageId || '',
						title: title || '',
						beforeUpdateRequest,
						forceTemplatePanelOpen,
						openContentInNewTab,
						...(contentId && { contentId }),
						...(onContentUpdated && {
							onContentUpdated: onContentUpdatedWrapper,
						}),
						...(onVariableInputError && { onError: onVariableInputError }),
					});
				} else {
					const legacyVariablesUrl = CREATE_PAGE_VARIABLES_ACTION_LEGACY.toUrl(
						{},
						{
							query: {
								templateId,
								spaceKey,
								title: title || '',
								newSpaceKey: spaceKey,
								fromPageId: parentPageId || '',
								// Try to get window.AJS if it doesn't exist, return nothing
								// @ts-ignore
								atl_token: (window as any).AJS?.Meta?.get('atl-token') || '',
							},
						},
					);

					// use forceReload because the SPA routing for this legacy page is broken
					navigate(legacyVariablesUrl, {
						openContentInNewTab,
						forceReload: true,
					});

					experienceTracker.succeed({ name: CREATE_PAGE_EXPERIENCE });
				}
				return false;
			} else if (contentId) {
				beforeUpdateRequest && beforeUpdateRequest();
				const updateFromTemplateResponse = await updateFromTemplateMutation({
					variables: {
						pageAri: ConfluencePageAri.create({
							siteId: cloudId,
							pageId: contentId,
						}).toString(),
						templateId: template.templateId,
						title,
						spaceKey,
					},
				});

				const response = updateFromTemplateResponse?.data?.confluence_updateInstance;

				const responseEditorIsFabric = !!response?.content?.metadata?.simple?.isFabric;

				if (responseEditorIsFabric && onContentUpdated && response) {
					onContentUpdated(response, template, spaceKey, contentId);
				} else {
					const draftUrl = response?.content?.links?.editui;
					navigate(processDraftUrl(draftUrl), {
						isReplaceAction: true,
					});
				}
			} else {
				// ToDo: Set attemptSpaUrlResponse to always be true and verify that there are no longer editor->editor
				//  transition bugs (https://product-fabric.atlassian.net/browse/MODES-3741)
				//  remove the call to isEditorPage if successful
				const createFromTemplateResponse = await createFromTemplateMutation({
					variables: {
						spaceKey,
						parentPageId,
						title,
						subType,
						blueprint: JSON.stringify(template),
						attemptSpaUrlResponse: !isEditorPage,
					},
				});
				const draftUrl =
					createFromTemplateResponse?.data?.experimentalPublishPageByTemplate?.links?.draftUrl;
				if (forceTemplatePanelOpen) {
					navigate(
						addQueryParams(processDraftUrl(draftUrl), {
							[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: IN_EDITOR_TEMPLATES_QUERY_OPEN,
							[CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME]: 'true',
						}),
						{
							openContentInNewTab,
						},
					);
				} else {
					navigate(
						addQueryParams(processDraftUrl(draftUrl), {
							[CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME]: 'true',
						}),
						{
							openContentInNewTab,
						},
					);
				}
				experienceTracker.succeed({ name: CREATE_PAGE_EXPERIENCE });
			}
			return true;
		},
		[
			createFromTemplateMutation,
			updateFromTemplateMutation,
			isEditorPage,
			experienceTracker,
			navigate,
			openTemplateVariables,
			cloudId,
		],
	);

	return useMemo(() => ({ canHandle, handle }), [canHandle, handle]);
};
